/**
 * GETGRIDGATE App Links Utility
 * Central source of truth for building app links to external sites.
 * Handles both direct URLs and SSO deep links.
 */

// Cache for app links config
let appLinksCache = null;
let navConfigCache = null;
let cacheTimestamp = null;
const CACHE_TTL = 60000; // 1 minute

// Default return paths for each app
const DEFAULT_RETURN_PATHS = {
  pcc: '/workorders',
  quickcanvass: '/staffing-requests',
  stationlogbook: '/',
  askstewie: '/',
  timesheets: '/',
  rfid: '/',
};

/**
 * Fetch and cache app links configuration
 */
export async function fetchAppLinksConfig(apiClient) {
  const now = Date.now();
  
  // Return cached if still valid
  if (appLinksCache && cacheTimestamp && (now - cacheTimestamp) < CACHE_TTL) {
    return { links: appLinksCache, navConfig: navConfigCache };
  }
  
  try {
    const response = await apiClient.get('/config/app-links');
    appLinksCache = response.data?.links || {};
    navConfigCache = response.data?.nav_config || { sso_deep_links_enabled: false, open_in_new_tab: false };
    cacheTimestamp = now;
    return { links: appLinksCache, navConfig: navConfigCache };
  } catch (error) {
    console.error('Failed to fetch app links config:', error);
    return { links: {}, navConfig: { sso_deep_links_enabled: false, open_in_new_tab: false } };
  }
}

/**
 * Build a link to an external app
 * @param {string} appKey - The app key (pcc, quickcanvass, stationlogbook, askstewie, timesheets, rfid)
 * @param {string} returnToPath - Optional path to return to after SSO (default: app's default path)
 * @param {object} options - Additional options { links, navConfig } from cached config
 * @returns {object} { url: string, isExternal: boolean, openInNewTab: boolean, comingSoon: boolean }
 */
export function buildAppLink(appKey, returnToPath = null, options = {}) {
  const links = options.links || appLinksCache || {};
  const navConfig = options.navConfig || navConfigCache || {};
  
  const linkInfo = links[appKey];
  
  // Timesheets is ALWAYS internal - never external redirect
  if (appKey === 'timesheets' || linkInfo?.is_internal) {
    return {
      url: '/timesheets' + (returnToPath || ''),
      isExternal: false,
      openInNewTab: false,
      comingSoon: false,
    };
  }
  
  if (!linkInfo || !linkInfo.url) {
    // Fallback to internal routes if no external URL configured
    const fallbackRoutes = {
      pcc: '/command-center',
      quickcanvass: '/quickcanvass',
      stationlogbook: '/station-log',
      askstewie: '/askstewie',
      timesheets: '/timesheets',
      rfid: '/rfid',
    };
    return {
      url: fallbackRoutes[appKey] || '/dashboard',
      isExternal: false,
      openInNewTab: false,
      comingSoon: linkInfo?.coming_soon || false,
    };
  }
  
  const baseUrl = linkInfo.url;
  const path = returnToPath || linkInfo.sso_return_path || DEFAULT_RETURN_PATHS[appKey] || '/';
  
  let url;
  if (navConfig.sso_deep_links_enabled && !linkInfo.is_internal) {
    // SSO deep link format
    url = `${baseUrl}/sso/start?return_to=${encodeURIComponent(path)}&from=getgridgate`;
  } else {
    // Direct URL (no SSO)
    url = baseUrl;
  }
  
  return {
    url,
    isExternal: true,
    openInNewTab: navConfig.open_in_new_tab || false,
    comingSoon: linkInfo.coming_soon || false,
  };
}

/**
 * Navigate to an external app
 * @param {string} appKey - The app key
 * @param {string} returnToPath - Optional path
 * @param {object} options - Config options
 */
export function navigateToApp(appKey, returnToPath = null, options = {}) {
  const link = buildAppLink(appKey, returnToPath, options);
  
  if (link.comingSoon) {
    // Return info for caller to show "Coming Soon" UI
    return { blocked: true, reason: 'coming_soon', appKey };
  }
  
  if (link.isExternal) {
    if (link.openInNewTab) {
      window.open(link.url, '_blank', 'noopener,noreferrer');
    } else {
      window.location.href = link.url;
    }
    return { blocked: false, navigated: true, url: link.url };
  } else {
    // Return URL for React Router navigation
    return { blocked: false, navigated: false, url: link.url, useRouter: true };
  }
}

/**
 * Build external URL for a feed item link
 * Called by backend when generating feed items - this is the frontend version for verification
 * @param {string} appKey - The app key
 * @param {string} path - The path within the app
 * @param {object} options - Config options
 * @returns {string} Full external URL
 */
export function buildExternalFeedLink(appKey, path = '/', options = {}) {
  const links = options.links || appLinksCache || {};
  const navConfig = options.navConfig || navConfigCache || {};
  
  const linkInfo = links[appKey];
  if (!linkInfo || !linkInfo.url) {
    return null;
  }
  
  const baseUrl = linkInfo.url;
  
  if (navConfig.sso_deep_links_enabled && !linkInfo.is_internal) {
    return `${baseUrl}/sso/start?return_to=${encodeURIComponent(path)}&from=getgridgate`;
  } else {
    return `${baseUrl}${path}`;
  }
}

/**
 * Clear the cache (useful for testing or after config changes)
 */
export function clearAppLinksCache() {
  appLinksCache = null;
  navConfigCache = null;
  cacheTimestamp = null;
}

export default {
  fetchAppLinksConfig,
  buildAppLink,
  navigateToApp,
  buildExternalFeedLink,
  clearAppLinksCache,
  DEFAULT_RETURN_PATHS,
};
